import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { RiskView, buildRiskRows } from '@/components/positions/RiskView';
import { RebuildingBanner } from '@/components/positions/RebuildingBanner';
import { SchwabLink } from '@/components/SchwabLink';
import { ShowFillsToggle } from '@/components/ui/ShowFillsToggle';
import { schwabResearchUrl } from '@/lib/schwabLinks';
import { stockApi, type StockPosition } from '@/lib/api/client';
import { useChains } from '@/hooks/queries/useChains';
import { useQuotes, useRiskBoardGreeks } from '@/hooks/queries/useQuotes';
import { useShowFills } from '@/hooks/useShowFills';
import { useUrlParam } from '@/hooks/useUrlState';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { consolidateLegs, type ConsolidatedLeg } from '@/lib/consolidateLegs';
import { cn } from '@/lib/utils';
import type { TradeChainWithPositions, PositionLeg } from '@steward/shared';

type ViewMode = 'portfolio' | 'all' | 'risk';

const VIEW_MODES: ViewMode[] = ['portfolio', 'all', 'risk'];

interface HoldingGroup {
  symbol: string;
  totalShares: number;
  avgCostBasis: number;
  totalCostBasis: number;
  positions: StockPosition[];
  hasOptionChains: boolean;
}

function originLabel(origin: string) {
  switch (origin) {
    case 'assignment': return 'Assignment';
    case 'exercise': return 'Exercise';
    case 'direct_purchase': return 'Purchase';
    default: return origin;
  }
}

function daysHeld(openedAt: Date | string, closedAt: Date | string | null): number {
  const start = new Date(openedAt).getTime();
  const end = closedAt ? new Date(closedAt).getTime() : Date.now();
  return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
}

function StockHoldingDetail({ symbol }: { symbol: string }) {
  const { data: allPositions, isLoading: posLoading } = useQuery({
    queryKey: ['stock-positions', symbol, 'all'],
    queryFn: () => stockApi.getAllBySymbol(symbol),
  });

  const { data: chains, isLoading: chainsLoading } = useChains({ symbol });

  const openPositions = allPositions?.filter(p => p.status === 'open') ?? [];

  // Find coinciding option chains: chains that were active during the holding period
  const coincidingChains = useMemo(() => {
    if (!chains || !openPositions.length) return [];
    const earliestOpen = Math.min(...openPositions.map(p => new Date(p.openedAt).getTime()));

    return chains.filter(chain => {
      const chainOpen = new Date(chain.openedAt).getTime();
      const chainClose = chain.closedAt ? new Date(chain.closedAt).getTime() : Infinity;
      // Chain overlaps with holding period if it opened before now and closed after earliest stock open
      return chainClose >= earliestOpen && chainOpen <= Date.now();
    }).sort((a, b) => {
      // Open chains first, then by date
      if (a.status !== b.status) {
        if (a.status === 'open' || a.status === 'partial') return -1;
        if (b.status === 'open' || b.status === 'partial') return 1;
      }
      return new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime();
    });
  }, [chains, openPositions]);

  if (posLoading || chainsLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stock Lots */}
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Stock Lots ({openPositions.length})
        </h4>
        <div className="space-y-2">
          {openPositions.map((pos) => (
            <div key={pos.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 bg-gray-50 rounded-lg px-3 py-2 text-sm">
              <div className="flex items-center gap-3">
                <span className="font-mono font-medium text-gray-900">
                  {pos.quantityRemaining.toLocaleString()} shares
                </span>
                <span className="text-xs text-gray-500">
                  @ ${pos.costBasisPerShare.toFixed(2)}
                </span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                  {originLabel(pos.origin)}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>Acquired {formatDate(pos.openedAt)}</span>
                <span className="font-medium text-gray-700">{daysHeld(pos.openedAt, pos.closedAt)}d held</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coinciding Option Chains */}
      {coincidingChains.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Option Chains During Holding ({coincidingChains.length})
          </h4>
          <div className="space-y-1">
            {coincidingChains.map((chain) => {
              const netPremium = chain.totalPremiumCollected - chain.totalPremiumPaid;
              const openPos = chain.positions.find(p => p.status === 'open');
              const strikes = openPos?.legs
                .map(l => `$${l.strikePrice}${l.optionType === 'put' ? 'P' : 'C'}`)
                .join(' / ') ?? '';

              return (
                <Link
                  key={chain.id}
                  to={`/chains/${chain.id}`}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg px-3 py-2 text-sm hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <StrategyBadge strategy={chain.strategyType} size="sm" />
                    <StatusBadge status={chain.status} size="sm" />
                    {strikes && (
                      <span className="text-xs font-mono text-gray-500">{strikes}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {formatDate(chain.openedAt)}
                      {chain.closedAt ? ` – ${formatDate(chain.closedAt)}` : ' – now'}
                    </span>
                    <span className={cn('font-mono text-xs font-medium', netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
                      {formatCurrency(netPremium)}
                    </span>
                    {chain.status === 'closed' && (
                      <PnlDisplay value={chain.realizedPnl} size="sm" />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-2 text-right">
            <Link
              to={`/symbols/${symbol}`}
              className="text-xs text-brand-600 hover:text-brand-700"
            >
              View all {symbol} chains &rarr;
            </Link>
          </div>
        </div>
      )}

      {coincidingChains.length === 0 && (
        <p className="text-xs text-gray-400">No option chains during holding period</p>
      )}
    </div>
  );
}

export interface PositionRow {
  symbol: string;
  underlying: string;
  type: 'equity' | 'option';
  qty: number;
  costBasis: number;      // total cost basis
  costPerUnit: number;     // per share or per contract
  chainId?: string;
  /** Fill lots merged into this row (see consolidateLegs). 1 outside "Show fills" mode's raw rows. */
  fillCount: number;
}

function formatOptionLabel(leg: { optionType: string; strikePrice: number; expirationDate: string | Date }) {
  const exp = new Date(leg.expirationDate);
  const m = String(exp.getUTCMonth() + 1).padStart(2, '0');
  const d = String(exp.getUTCDate()).padStart(2, '0');
  const y = exp.getUTCFullYear();
  const typeLabel = leg.optionType === 'put' ? 'P' : 'C';
  return `${m}/${d}/${y} ${leg.strikePrice.toFixed(2)} ${typeLabel}`;
}

/**
 * Builds the option rows for one position within one chain, consolidating
 * same-contract fill lots first (reusing consolidateLegs -- see task-20
 * brief) so `PositionRow` inherits a correct weighted-average `costPerUnit`
 * with no separate averaging logic of its own. Consolidating scoped to a
 * single position (rather than across the whole underlying) keeps each row
 * tied to the one chain it came from -- two different chains on the same
 * underlying/strike/expiration/side must never merge into one row and lose
 * a `chainId`.
 */
export function legsToPositionRows(
  legs: PositionLeg[],
  chain: { id: string; underlyingSymbol: string },
  showFills: boolean
): PositionRow[] {
  const activeLegs = legs.filter((leg) => leg.quantityRemaining > 0);
  const rows: ConsolidatedLeg[] = showFills
    ? activeLegs.map((leg) => ({ ...leg, fillCount: 1 }))
    : consolidateLegs(activeLegs);

  return rows.map((leg) => {
    const isShort = leg.action === 'STO' || leg.action === 'STC';
    return {
      symbol: `${chain.underlyingSymbol} ${formatOptionLabel(leg)}`,
      underlying: chain.underlyingSymbol,
      type: 'option',
      qty: isShort ? -leg.quantityRemaining : leg.quantityRemaining,
      costBasis: leg.pricePerContract * leg.quantityRemaining * 100 * (isShort ? -1 : 1),
      costPerUnit: leg.pricePerContract,
      chainId: chain.id,
      fillCount: leg.fillCount,
    };
  });
}

interface SymbolGroup {
  symbol: string;
  stock: { qty: number; costBasis: number; costPerUnit: number } | null;
  options: PositionRow[];
}

function AllPositionsView({
  stockPositions,
  chains,
  quotes,
}: {
  stockPositions: StockPosition[] | undefined;
  chains: ReturnType<typeof useChains>['data'];
  quotes: Record<string, { price: number; change: number; changePercent: number }> | undefined;
}) {
  const [showFills, setShowFills] = useShowFills();

  const symbolGroups = useMemo(() => {
    const groups = new Map<string, SymbolGroup>();

    const getGroup = (sym: string): SymbolGroup => {
      let g = groups.get(sym);
      if (!g) { g = { symbol: sym, stock: null, options: [] }; groups.set(sym, g); }
      return g;
    };

    // Aggregate stock positions by symbol
    if (stockPositions) {
      for (const sp of stockPositions) {
        const g = getGroup(sp.underlyingSymbol);
        if (g.stock) {
          g.stock.qty += sp.quantityRemaining;
          g.stock.costBasis += sp.quantityRemaining * sp.costBasisPerShare;
          g.stock.costPerUnit = g.stock.costBasis / g.stock.qty;
        } else {
          g.stock = {
            qty: sp.quantityRemaining,
            costBasis: sp.quantityRemaining * sp.costBasisPerShare,
            costPerUnit: sp.costBasisPerShare,
          };
        }
      }
    }

    // Add option legs -- consolidated to one row per contract by default
    // (see legsToPositionRows); "Show fills" reveals the raw per-fill rows.
    if (chains) {
      for (const chain of chains) {
        if (chain.status !== 'open' && chain.status !== 'partial') continue;
        for (const pos of chain.positions) {
          if (pos.status !== 'open') continue;
          const g = getGroup(chain.underlyingSymbol);
          g.options.push(...legsToPositionRows(pos.legs, chain, showFills));
        }
      }
    }

    // Sort option legs within each group
    for (const g of groups.values()) {
      g.options.sort((a, b) => a.symbol.localeCompare(b.symbol));
    }

    return [...groups.values()].sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [stockPositions, chains, showFills]);

  // Totals
  const totals = useMemo(() => {
    let stockValue = 0;
    let stockCost = 0;
    let optionCost = 0;
    let stockCount = 0;
    let optionCount = 0;

    for (const g of symbolGroups) {
      if (g.stock) {
        stockCount++;
        stockCost += g.stock.costBasis;
        const price = quotes?.[g.symbol]?.price;
        if (price) stockValue += price * g.stock.qty;
      }
      optionCount += g.options.length;
      optionCost += g.options.reduce((sum, o) => sum + Math.abs(o.costBasis), 0);
    }

    return { stockValue, stockCost, stockPnl: stockValue - stockCost, optionCost, stockCount, optionCount };
  }, [symbolGroups, quotes]);

  if (symbolGroups.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-gray-500">No open positions</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Symbols</p>
          <p className="text-xl font-semibold text-gray-900">{symbolGroups.length}</p>
          <p className="text-xs text-gray-400">{totals.stockCount} stock · {totals.optionCount} legs</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Stock Value</p>
          <p className="text-xl font-semibold text-gray-900">
            {totals.stockValue > 0 ? formatCurrency(totals.stockValue) : '-'}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Stock Cost Basis</p>
          <p className="text-xl font-semibold text-gray-900">
            {totals.stockCost > 0 ? formatCurrency(totals.stockCost) : '-'}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Unrealized P&L</p>
          {totals.stockValue > 0 ? (
            <PnlDisplay value={totals.stockPnl} size="lg" />
          ) : (
            <p className="text-xl font-semibold text-gray-400">-</p>
          )}
        </Card>
      </div>

      <div className="flex justify-end">
        <ShowFillsToggle showFills={showFills} onChange={setShowFills} />
      </div>

      <Card>
        <CardContent className="p-0">
          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-200">
                  <th className="text-left py-2 px-4 font-medium">Symbol</th>
                  <th className="text-right py-2 px-4 font-medium">Qty</th>
                  <th className="text-right py-2 px-4 font-medium">Price</th>
                  <th className="text-right py-2 px-4 font-medium">Mkt Val</th>
                  <th className="text-right py-2 px-4 font-medium">Cost Basis</th>
                  <th className="text-right py-2 px-4 font-medium">Cost/Unit</th>
                  <th className="text-right py-2 px-4 font-medium">Gain/Loss</th>
                </tr>
              </thead>
              <tbody>
                {symbolGroups.map((g) => {
                  const quote = quotes?.[g.symbol];
                  const price = quote?.price ?? null;
                  const stockMktVal = g.stock && price ? price * g.stock.qty : null;
                  const stockGainLoss = stockMktVal !== null && g.stock ? stockMktVal - g.stock.costBasis : null;

                  return (
                    <React.Fragment key={g.symbol}>
                      {/* Symbol header / stock row */}
                      {g.stock ? (
                        <tr className="border-b border-gray-100 bg-white">
                          <td className="py-2.5 px-4">
                            <div className="flex items-center gap-0.5">
                              <Link to={`/symbols/${g.symbol}`} className="font-semibold text-brand-600 hover:text-brand-700">
                                {g.symbol}
                              </Link>
                              <SchwabLink href={schwabResearchUrl(g.symbol)} label={`${g.symbol} on Schwab`} ariaLabel={`Research ${g.symbol} on Schwab`} />
                            </div>
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                            {g.stock.qty.toLocaleString()}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                            {price ? `$${price.toFixed(2)}` : '-'}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                            {stockMktVal !== null ? formatCurrency(stockMktVal) : '-'}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono text-gray-500">
                            {formatCurrency(g.stock.costBasis)}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono text-gray-500">
                            ${g.stock.costPerUnit.toFixed(2)}
                          </td>
                          <td className="py-2.5 px-4 text-right">
                            {stockGainLoss !== null ? (
                              <span className={cn('font-mono font-medium', stockGainLoss >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {stockGainLoss >= 0 ? '+' : ''}{formatCurrency(stockGainLoss)}
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      ) : (
                        // Options-only symbol header
                        <tr className="border-b border-gray-100 bg-white">
                          <td className="py-2.5 px-4">
                            <div className="flex items-center gap-0.5">
                              <Link to={`/symbols/${g.symbol}`} className="font-semibold text-brand-600 hover:text-brand-700">
                                {g.symbol}
                              </Link>
                              <SchwabLink href={schwabResearchUrl(g.symbol)} label={`${g.symbol} on Schwab`} ariaLabel={`Research ${g.symbol} on Schwab`} />
                            </div>
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono text-gray-400">-</td>
                          <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                            {price ? `$${price.toFixed(2)}` : '-'}
                          </td>
                          <td colSpan={4} className="py-2.5 px-4"></td>
                        </tr>
                      )}

                      {/* Option legs */}
                      {g.options.map((opt, idx) => (
                        <tr key={`${g.symbol}-opt-${idx}`} className="border-b border-gray-50 bg-gray-50/40">
                          <td className="py-2 px-4 pl-8">
                            {opt.chainId ? (
                              <Link to={`/chains/${opt.chainId}`} className="text-xs font-mono text-gray-600 hover:text-brand-600">
                                {opt.symbol}
                              </Link>
                            ) : (
                              <span className="text-xs font-mono text-gray-600">{opt.symbol}</span>
                            )}
                          </td>
                          <td className="py-2 px-4 text-right font-mono text-xs text-gray-600">
                            {opt.qty.toLocaleString()}
                            {opt.fillCount > 1 && (
                              <span className="text-gray-400 ml-1">({opt.fillCount} fills)</span>
                            )}
                          </td>
                          <td className="py-2 px-4 text-right font-mono text-gray-400">-</td>
                          <td className="py-2 px-4 text-right font-mono text-gray-400">-</td>
                          <td className="py-2 px-4 text-right font-mono text-xs text-gray-500">
                            {formatCurrency(Math.abs(opt.costBasis))}
                          </td>
                          <td className="py-2 px-4 text-right font-mono text-xs text-gray-500">
                            ${opt.costPerUnit.toFixed(4)}
                          </td>
                          <td className="py-2 px-4 text-right font-mono text-gray-400">-</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                  <td className="py-3 px-4 text-gray-900">Total</td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-right font-mono text-gray-900">
                    {totals.stockValue > 0 ? formatCurrency(totals.stockValue) : '-'}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-gray-700">
                    {totals.stockCost > 0 ? formatCurrency(totals.stockCost) : '-'}
                  </td>
                  <td className="py-3 px-4"></td>
                  <td className="py-3 px-4 text-right">
                    {totals.stockValue > 0 ? (
                      <span className={cn('font-mono', totals.stockPnl >= 0 ? 'text-green-600' : 'text-red-600')}>
                        {totals.stockPnl >= 0 ? '+' : ''}{formatCurrency(totals.stockPnl)}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden divide-y divide-gray-100">
            {symbolGroups.map((g) => {
              const quote = quotes?.[g.symbol];
              const price = quote?.price ?? null;
              const stockMktVal = g.stock && price ? price * g.stock.qty : null;
              const stockGainLoss = stockMktVal !== null && g.stock ? stockMktVal - g.stock.costBasis : null;

              return (
                <div key={g.symbol}>
                  {/* Symbol header */}
                  <div className="p-3 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link to={`/symbols/${g.symbol}`} className="font-semibold text-brand-600">
                          {g.symbol}
                        </Link>
                        <SchwabLink href={schwabResearchUrl(g.symbol)} label={`${g.symbol} on Schwab`} ariaLabel={`Research ${g.symbol} on Schwab`} />
                        {price && <span className="text-xs text-gray-500">${price.toFixed(2)}</span>}
                      </div>
                      {g.stock && (
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{g.stock.qty.toLocaleString()} shares</p>
                          {stockMktVal !== null && (
                            <p className="font-mono text-sm text-gray-900">{formatCurrency(stockMktVal)}</p>
                          )}
                          {stockGainLoss !== null && (
                            <p className={cn('font-mono text-xs', stockGainLoss >= 0 ? 'text-green-600' : 'text-red-600')}>
                              {stockGainLoss >= 0 ? '+' : ''}{formatCurrency(stockGainLoss)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Option legs */}
                  {g.options.map((opt, idx) => (
                    <div key={`${g.symbol}-opt-${idx}`} className="px-3 py-2 pl-6 bg-gray-50/50">
                      <div className="flex items-start justify-between">
                        <div>
                          {opt.chainId ? (
                            <Link to={`/chains/${opt.chainId}`} className="text-xs font-mono text-gray-600 hover:text-brand-600">
                              {opt.symbol}
                            </Link>
                          ) : (
                            <span className="text-xs font-mono text-gray-600">{opt.symbol}</span>
                          )}
                          <p className="text-xs text-gray-400">
                            Qty: {opt.qty}
                            {opt.fillCount > 1 && <span className="ml-1">({opt.fillCount} fills)</span>}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-xs text-gray-500">{formatCurrency(Math.abs(opt.costBasis))}</p>
                          <p className="text-xs text-gray-400">@ ${opt.costPerUnit.toFixed(4)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export interface OpenOptionChainMobileCardProps {
  chain: TradeChainWithPositions;
  quote: { price: number; change: number; changePercent: number } | undefined;
}

/**
 * Presentational: no query hooks (only useNavigate, which is fine to render
 * in a MemoryRouter-wrapped test), so it renders in a test with plain props.
 *
 * The whole card navigates on click/Enter/Space, but the container is a
 * `div[role="link"]`, not an `<a>` — SchwabLink is a real nested `<a
 * target="_blank">`, and an anchor inside an anchor is invalid HTML (React's
 * dev-mode DOM nesting validator flags it, and assistive tech sees two
 * overlapping interactive elements). Previously this was `<Link>` wrapping
 * everything, which produced exactly that nested-anchor DOM.
 */
export function OpenOptionChainMobileCard({ chain, quote }: OpenOptionChainMobileCardProps) {
  const navigate = useNavigate();
  const netPremium = chain.totalPremiumCollected - chain.totalPremiumPaid;
  const openPos = chain.positions.find(p => p.status === 'open');
  // Same consolidation as the desktop OpenOptionChainsTable (Task 21d): dedupe fill lots so a
  // split-fill condor shows 4 unique strikes, not one repeated entry per fill.
  const { strikes, contracts } = strikesAndContracts(openPos?.legs ?? []);
  const expDate = openPos?.legs[0]?.expirationDate;
  const dte = expDate ? Math.ceil((new Date(expDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

  const goToChain = () => navigate(`/chains/${chain.id}`);

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToChain}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToChain();
        }
      }}
      className="block p-4 hover:bg-gray-50 cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{chain.underlyingSymbol}</span>
            <SchwabLink href={schwabResearchUrl(chain.underlyingSymbol)} label={`${chain.underlyingSymbol} on Schwab`} ariaLabel={`Research ${chain.underlyingSymbol} on Schwab`} />
            {quote?.price && <span className="text-xs text-gray-500">${quote.price.toFixed(2)}</span>}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {chain.strategyType?.replace(/_/g, ' ')}
            {contracts !== null ? ` ×${contracts}` : ''}
            {' · '}{strikes}
            {dte !== null && (
              <span className={cn('ml-1', dte <= 7 ? 'text-yellow-600' : '')}> · {dte}d</span>
            )}
          </p>
        </div>
        <span className={cn('font-mono text-sm', netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
          {formatCurrency(netPremium)}
        </span>
      </div>
    </div>
  );
}

export interface OpenOptionChainsTableProps {
  chains: TradeChainWithPositions[];
  quotes: Record<string, { price: number; change: number; changePercent: number }> | undefined;
}

/**
 * Consolidates each chain's open-position legs before rendering (reusing Task 20's
 * `consolidateLegs`), so the Strikes cell shows one entry per distinct contract rather than
 * one per fill lot -- a 12-lot iron condor filled 1+3+6 per leg would otherwise repeat
 * "$400P" three times instead of showing 4 unique strikes. The Strategy cell's "×N" suffix is
 * the consolidated quantity of the row's largest leg (not a min/max range, and not a
 * total-contracts sum across legs) -- for a uniform chain (the common case) every leg's
 * consolidated quantity is the same number, and picking the largest is a defined, honest answer
 * even when legs differ (a covered call reduced on one side, say) instead of inventing a
 * "typical" count.
 */
function strikesAndContracts(legs: PositionLeg[]): { strikes: string; contracts: number | null } {
  if (legs.length === 0) return { strikes: '-', contracts: null };
  const consolidated = consolidateLegs(legs);
  const strikes = consolidated
    .map((l) => `$${l.strikePrice}${l.optionType === 'put' ? 'P' : 'C'}`)
    .join(' / ');
  const contracts = Math.max(...consolidated.map((l) => l.quantity));
  return { strikes, contracts };
}

/** Presentational: no query hooks, so it renders in a test with plain props. */
export function OpenOptionChainsTable({ chains, quotes }: OpenOptionChainsTableProps) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-xs text-gray-500 border-b border-gray-200">
          <th className="text-left py-2 px-4 font-medium">Symbol</th>
          <th className="text-left py-2 px-4 font-medium">Strategy</th>
          <th className="text-right py-2 px-4 font-medium">Price</th>
          <th className="text-right py-2 px-4 font-medium">Strikes</th>
          <th className="text-right py-2 px-4 font-medium">Expiration</th>
          <th className="text-right py-2 px-4 font-medium">Net Premium</th>
        </tr>
      </thead>
      <tbody>
        {chains.map((chain) => {
          const quote = quotes?.[chain.underlyingSymbol];
          const netPremium = chain.totalPremiumCollected - chain.totalPremiumPaid;
          const openPos = chain.positions.find(p => p.status === 'open');
          const { strikes, contracts } = strikesAndContracts(openPos?.legs ?? []);
          const expDate = openPos?.legs[0]?.expirationDate;
          const dte = expDate ? Math.ceil((new Date(expDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;

          return (
            <tr key={chain.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-3 px-4">
                <div className="flex items-center gap-0.5">
                  <Link
                    to={`/chains/${chain.id}`}
                    className="font-semibold text-brand-600 hover:text-brand-700"
                  >
                    {chain.underlyingSymbol}
                  </Link>
                  <SchwabLink href={schwabResearchUrl(chain.underlyingSymbol)} label={`${chain.underlyingSymbol} on Schwab`} ariaLabel={`Research ${chain.underlyingSymbol} on Schwab`} />
                </div>
              </td>
              <td className="py-3 px-4 text-gray-600 capitalize">
                {chain.strategyType?.replace(/_/g, ' ') ?? '-'}
                {contracts !== null ? ` ×${contracts}` : ''}
              </td>
              <td className="py-3 px-4 text-right font-mono text-gray-900">
                {quote?.price ? `$${quote.price.toFixed(2)}` : '-'}
              </td>
              <td className="py-3 px-4 text-right font-mono text-gray-600 text-xs">
                {strikes}
              </td>
              <td className="py-3 px-4 text-right">
                {dte !== null ? (
                  <span className={cn(
                    'text-sm',
                    dte <= 7 && dte >= 0 ? 'text-yellow-600 font-medium' : 'text-gray-600'
                  )}>
                    {dte}d
                  </span>
                ) : '-'}
              </td>
              <td className="py-3 px-4 text-right">
                <span className={cn('font-mono', netPremium >= 0 ? 'text-green-600' : 'text-red-600')}>
                  {formatCurrency(netPremium)}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function PositionsPage() {
  // View mode and the expanded stock row are both view state, so both live in the URL and a
  // link reopens the board exactly as it was left. The Risk view's Explain and Ask AI dialogs
  // deliberately do not: a dialog is a thing you opened, not a view you are looking at, and a
  // shared link that springs a modal open is worse than one that does not.
  const [viewMode, setViewMode] = useUrlParam<ViewMode>('view', 'all', VIEW_MODES);
  const [expandedSymbol, setExpandedSymbol] = useUrlParam('symbol', '');

  const { data: stockPositions, isLoading: stockLoading } = useQuery({
    queryKey: ['stock-positions', 'open'],
    queryFn: stockApi.getOpen,
  });

  const { data: chains, isLoading: chainsLoading } = useChains({ status: 'open' });

  // Get all symbols we need quotes for
  const allSymbols = useMemo(() => {
    const syms = new Set<string>();
    if (stockPositions) stockPositions.forEach(sp => syms.add(sp.underlyingSymbol));
    if (chains) chains.forEach(c => syms.add(c.underlyingSymbol));
    return [...syms];
  }, [stockPositions, chains]);

  const { data: quotes } = useQuotes(allSymbols);

  // The container owns the query; RiskView stays props-driven. `buildRiskRows` is pure and
  // memoised on both sides, so computing it here to shape the request costs nothing. The hook
  // itself must stay unconditional (hook order) — off the Risk view we hand it an empty array
  // instead, so its own `enabled: groups.length > 0` keeps it idle rather than fanning out up to
  // forty chain lookups on every Positions load, including the default 'all' view.
  const riskRows = useMemo(() => buildRiskRows(chains, quotes), [chains, quotes]);
  const { data: boardGreeks } = useRiskBoardGreeks(viewMode === 'risk' ? riskRows : []);

  // Group stock positions by symbol
  const stockHoldings = useMemo(() => {
    if (!stockPositions) return [];
    const chainSymbols = new Set(chains?.map(c => c.underlyingSymbol) ?? []);
    const map = new Map<string, HoldingGroup>();

    for (const sp of stockPositions) {
      const existing = map.get(sp.underlyingSymbol);
      if (existing) {
        existing.totalShares += sp.quantityRemaining;
        existing.totalCostBasis += sp.quantityRemaining * sp.costBasisPerShare;
        existing.positions.push(sp);
      } else {
        map.set(sp.underlyingSymbol, {
          symbol: sp.underlyingSymbol,
          totalShares: sp.quantityRemaining,
          avgCostBasis: sp.costBasisPerShare,
          totalCostBasis: sp.quantityRemaining * sp.costBasisPerShare,
          positions: [sp],
          hasOptionChains: chainSymbols.has(sp.underlyingSymbol),
        });
      }
    }

    // Recalculate avg cost basis
    for (const holding of map.values()) {
      holding.avgCostBasis = holding.totalCostBasis / holding.totalShares;
    }

    return [...map.values()].sort((a, b) => {
      const aVal = (quotes?.[a.symbol]?.price ?? 0) * a.totalShares;
      const bVal = (quotes?.[b.symbol]?.price ?? 0) * b.totalShares;
      return bVal - aVal;
    });
  }, [stockPositions, chains, quotes]);

  // Group open option chains by symbol
  const optionPositions = useMemo(() => {
    if (!chains) return [];
    return chains
      .filter(c => c.status === 'open' || c.status === 'partial')
      .sort((a, b) => {
        const aPrice = quotes?.[a.underlyingSymbol]?.price ?? 0;
        const bPrice = quotes?.[b.underlyingSymbol]?.price ?? 0;
        return bPrice - aPrice;
      });
  }, [chains, quotes]);

  const isLoading = stockLoading || chainsLoading;

  // Portfolio totals
  const totalStockValue = stockHoldings.reduce((sum, h) => {
    const price = quotes?.[h.symbol]?.price;
    return sum + (price ? price * h.totalShares : 0);
  }, 0);
  const totalStockCost = stockHoldings.reduce((sum, h) => sum + h.totalCostBasis, 0);
  const totalStockPnl = totalStockValue - totalStockCost;

  const toggleExpand = (symbol: string) => {
    setExpandedSymbol(expandedSymbol === symbol ? '' : symbol);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Positions</h1>
          <p className="text-sm text-gray-500 mt-1">Current stock and option holdings</p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
          <Button
            variant={viewMode === 'portfolio' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('portfolio')}
          >
            Portfolio
          </Button>
          <Button
            variant={viewMode === 'all' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('all')}
          >
            All Positions
          </Button>
          <Button
            variant={viewMode === 'risk' ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('risk')}
          >
            Risk
          </Button>
        </div>
      </div>

      <RebuildingBanner />

      {viewMode === 'all' && (
        <AllPositionsView
          stockPositions={stockPositions}
          chains={chains}
          quotes={quotes}
        />
      )}

      {viewMode === 'risk' && <RiskView chains={chains} quotes={quotes} greeks={boardGreeks ?? null} />}

      {viewMode === 'portfolio' && <>
      {/* Portfolio Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <p className="text-sm text-gray-500">Stock Holdings</p>
          <p className="text-xl font-semibold text-gray-900">{stockHoldings.length}</p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Stock Value</p>
          <p className="text-xl font-semibold text-gray-900">
            {totalStockValue > 0 ? formatCurrency(totalStockValue) : '-'}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Stock Cost Basis</p>
          <p className="text-xl font-semibold text-gray-900">
            {totalStockCost > 0 ? formatCurrency(totalStockCost) : '-'}
          </p>
        </Card>
        <Card>
          <p className="text-sm text-gray-500">Unrealized P&L</p>
          {totalStockValue > 0 ? (
            <PnlDisplay value={totalStockPnl} size="lg" />
          ) : (
            <p className="text-xl font-semibold text-gray-400">-</p>
          )}
        </Card>
      </div>

      {/* Stock Holdings */}
      {stockHoldings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Stock Holdings</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop table */}
            <div className="hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-200">
                    <th className="text-left py-2 px-4 font-medium">Symbol</th>
                    <th className="text-right py-2 px-4 font-medium">Shares</th>
                    <th className="text-right py-2 px-4 font-medium">Price</th>
                    <th className="text-right py-2 px-4 font-medium">Avg Cost</th>
                    <th className="text-right py-2 px-4 font-medium">Market Value</th>
                    <th className="text-right py-2 px-4 font-medium">Cost Basis</th>
                    <th className="text-right py-2 px-4 font-medium">Gain/Loss</th>
                    <th className="text-right py-2 px-4 font-medium">Return</th>
                  </tr>
                </thead>
                <tbody>
                  {stockHoldings.map((holding) => {
                    const quote = quotes?.[holding.symbol];
                    const marketValue = quote?.price ? quote.price * holding.totalShares : null;
                    const gainLoss = marketValue !== null ? marketValue - holding.totalCostBasis : null;
                    const returnPct = gainLoss !== null && holding.totalCostBasis > 0
                      ? (gainLoss / holding.totalCostBasis) * 100
                      : null;
                    const isExpanded = expandedSymbol === holding.symbol;

                    return (
                      <React.Fragment key={holding.symbol}>
                        <tr
                          data-testid={`stock-row-${holding.symbol}`}
                          aria-expanded={isExpanded}
                          className={cn(
                            'cursor-pointer transition-colors',
                            isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50',
                            'border-b border-gray-100'
                          )}
                          onClick={() => toggleExpand(holding.symbol)}
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1.5">
                              <svg
                                className={cn('w-3.5 h-3.5 text-gray-400 transition-transform', isExpanded && 'rotate-90')}
                                fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                              <Link
                                to={`/symbols/${holding.symbol}`}
                                className="font-semibold text-brand-600 hover:text-brand-700"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {holding.symbol}
                              </Link>
                              <SchwabLink href={schwabResearchUrl(holding.symbol)} label={`${holding.symbol} on Schwab`} ariaLabel={`Research ${holding.symbol} on Schwab`} />
                              {holding.hasOptionChains && (
                                <span className="text-[10px] text-gray-400">+ options</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-gray-900">
                            {holding.totalShares.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-gray-900">
                            {quote?.price ? `$${quote.price.toFixed(2)}` : '-'}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-gray-500">
                            ${holding.avgCostBasis.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-gray-900">
                            {marketValue !== null ? formatCurrency(marketValue) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right font-mono text-gray-500">
                            {formatCurrency(holding.totalCostBasis)}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {gainLoss !== null ? (
                              <span className={cn('font-mono font-medium', gainLoss >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {returnPct !== null ? (
                              <span className={cn('font-mono text-xs font-medium', returnPct >= 0 ? 'text-green-600' : 'text-red-600')}>
                                {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}%
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} className="p-0">
                              <div className="px-4 py-4 bg-blue-50/50 border-b border-gray-200" data-testid={`stock-detail-${holding.symbol}`}>
                                <StockHoldingDetail symbol={holding.symbol} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {stockHoldings.map((holding) => {
                const quote = quotes?.[holding.symbol];
                const marketValue = quote?.price ? quote.price * holding.totalShares : null;
                const gainLoss = marketValue !== null ? marketValue - holding.totalCostBasis : null;
                const returnPct = gainLoss !== null && holding.totalCostBasis > 0
                  ? (gainLoss / holding.totalCostBasis) * 100
                  : null;
                const isExpanded = expandedSymbol === holding.symbol;

                return (
                  <div key={holding.symbol}>
                    <div
                      data-testid={`stock-card-${holding.symbol}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      className={cn(
                        'p-4 cursor-pointer transition-colors',
                        isExpanded ? 'bg-blue-50' : 'hover:bg-gray-50'
                      )}
                      onClick={() => toggleExpand(holding.symbol)}
                      onKeyDown={(e) => {
                        // A div that expands on click needs the keyboard too, or the row is
                        // reachable by tab and then does nothing.
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpand(holding.symbol);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <svg
                            className={cn('w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 mt-0.5', isExpanded && 'rotate-90')}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div>
                            <div className="flex items-center gap-0.5">
                              <Link
                                to={`/symbols/${holding.symbol}`}
                                className="font-semibold text-brand-600 hover:text-brand-700"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {holding.symbol}
                              </Link>
                              <SchwabLink href={schwabResearchUrl(holding.symbol)} label={`${holding.symbol} on Schwab`} ariaLabel={`Research ${holding.symbol} on Schwab`} />
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {holding.totalShares.toLocaleString()} shares @ ${holding.avgCostBasis.toFixed(2)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {marketValue !== null && (
                            <p className="font-mono text-sm text-gray-900">{formatCurrency(marketValue)}</p>
                          )}
                          {gainLoss !== null && returnPct !== null && (
                            <p className={cn('font-mono text-xs', gainLoss >= 0 ? 'text-green-600' : 'text-red-600')}>
                              {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)} ({returnPct >= 0 ? '+' : ''}{returnPct.toFixed(1)}%)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-blue-50/50">
                        <StockHoldingDetail symbol={holding.symbol} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Open Option Positions */}
      {optionPositions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Open Option Chains</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <OpenOptionChainsTable chains={optionPositions} quotes={quotes} />
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {optionPositions.map((chain) => (
                <OpenOptionChainMobileCard key={chain.id} chain={chain} quote={quotes?.[chain.underlyingSymbol]} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {stockHoldings.length === 0 && optionPositions.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No open positions</p>
          </CardContent>
        </Card>
      )}
      </>}
    </div>
  );
}
